import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLockQueue,
  getLockSchedule,
  getLockConfig,
  processQueue,
  processAllContracts,
  runOverdueCheck,
  getOverdueDashboard,
} from '@/features/lock-engine/api';
import type {
  QueuedActionModel,
  ScheduleInfoModel,
  LockEngineConfigModel,
  OverdueDashboardModel,
} from '@/features/lock-engine/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Lock,
  Unlock,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Zap,
  Settings,
  AlertTriangle,
  Play,
  RefreshCw,
  Calendar,
  Moon,
  Star,
  CheckCircle2,
  XCircle,
  ListChecks,
  Activity,
} from '@/lib/icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useToast } from '@/hooks/use-toast';
import { ButtonSpinner } from '@/components/feedback';
import { cn } from '@/lib/utils';
import { PortfolioEscalationCard } from '@/features/lock-engine/portfolio-escalation-card';
import { formatDateTime } from '@/lib/dates';
import { TimeZoneHint } from '@/components/common/time-zone-hint';

export const Route = createFileRoute('/_layout/lock-engine/')({
  component: LockEnginePage,
});

// ── Helpers ──────────────────────────────────────────────────

const escalationBadge = (level: string) => {
  const map: Record<
    string,
    { cls: string; label: string; icon: React.ReactNode }
  > = {
    none: {
      cls: 'bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400',
      label: 'None',
      icon: <ShieldCheck className="w-3 h-3" />,
    },
    warning: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Warning',
      icon: <AlertTriangle className="w-3 h-3" />,
    },
    partial_lock: {
      cls: 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400',
      label: 'Partial Lock',
      icon: <ShieldAlert className="w-3 h-3" />,
    },
    full_lock: {
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      label: 'Full Lock',
      icon: <Lock className="w-3 h-3" />,
    },
    defaulted: {
      cls: 'bg-rose-500/15 text-rose-600 border-rose-500/30 dark:text-rose-400',
      label: 'Defaulted',
      icon: <XCircle className="w-3 h-3" />,
    },
  };
  const s = map[level] || map.none;
  return (
    <Badge className={cn('gap-1', s.cls)}>
      {s.icon} {s.label}
    </Badge>
  );
};

const actionBadge = (action: string) => {
  if (
    action.toLowerCase().includes('lock') &&
    !action.toLowerCase().includes('unlock')
  ) {
    return (
      <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400 gap-1">
        <Lock className="w-3 h-3" /> Lock
      </Badge>
    );
  }
  if (action.toLowerCase().includes('unlock')) {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400 gap-1">
        <Unlock className="w-3 h-3" /> Unlock
      </Badge>
    );
  }
  return <Badge variant="outline">{action}</Badge>;
};

const fmtDateTime = (dt: string | null) => formatDateTime(dt, '---');

// ── Schedule Status Card ─────────────────────────────────────

function ScheduleCard({
  schedule,
  isLoading,
}: {
  schedule?: ScheduleInfoModel;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const canLock = schedule?.canLockNow ?? false;

  return (
    <Card className="border-0 shadow-md relative overflow-hidden">
      <div
        className={cn(
          'absolute inset-0 opacity-[0.05]',
          canLock ? 'bg-emerald-500' : 'bg-red-500'
        )}
      />
      <CardHeader className="pb-3 relative">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            Lock Schedule
          </CardTitle>
          <Badge
            className={cn(
              'text-sm px-3 py-1 font-semibold gap-1.5',
              canLock
                ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400'
                : 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400'
            )}
          >
            {canLock ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              <XCircle className="w-3.5 h-3.5" />
            )}
            {canLock ? 'ACTIVE' : 'RESTRICTED'}
          </Badge>
        </div>
        {schedule?.blackoutReason && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            {schedule.blackoutReason}
          </p>
        )}
      </CardHeader>
      <CardContent className="relative space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>Timezone</span>
          </div>
          <span className="font-medium">{schedule?.timezone ?? '---'}</span>

          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>Current Time</span>
          </div>
          <span className="font-medium font-mono text-xs">
            {schedule?.currentTime ?? '---'}
          </span>
        </div>

        {/* Restriction indicators */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Badge variant="outline" className="text-xs gap-1">
            <Moon className="w-3 h-3" />
            Night: {schedule?.nightBlackout?.enabled ? 'On' : 'Off'}
          </Badge>
          <Badge variant="outline" className="text-xs gap-1">
            <Star className="w-3 h-3" />
            Prayer: {schedule?.prayerTimes?.enabled ? 'On' : 'Off'}
          </Badge>
          <Badge variant="outline" className="text-xs gap-1">
            <Calendar className="w-3 h-3" />
            Friday: {schedule?.fridayRestriction?.enabled ? 'On' : 'Off'}
          </Badge>
          <Badge variant="outline" className="text-xs gap-1">
            <Moon className="w-3 h-3" />
            Ramadan: {schedule?.ramadan?.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Config Card ──────────────────────────────────────────────

function ConfigCard({
  config,
  isLoading,
}: {
  config?: LockEngineConfigModel;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const configItems = [
    {
      label: 'Lock Engine',
      value: config?.lockEnabled ? 'Enabled' : 'Disabled',
      active: config?.lockEnabled,
    },
    { label: 'Grace Period', value: `${config?.graceDays ?? 0} days` },
    {
      label: 'Partial Lock',
      value: `${config?.partialLockDays ?? 0} days overdue`,
    },
    { label: 'Full Lock', value: `${config?.fullLockDays ?? 0} days overdue` },
    { label: 'Default', value: `${config?.defaultDays ?? 0} days overdue` },
    {
      label: 'Prayer Times',
      value: config?.respectPrayerTimes ? 'Respected' : 'Ignored',
      active: config?.respectPrayerTimes,
    },
    {
      label: 'Fridays',
      value: config?.respectFridays ? 'Restricted' : 'Normal',
      active: config?.respectFridays,
    },
    {
      label: 'Ramadan',
      value: config?.respectRamadan ? 'Restricted' : 'Normal',
      active: config?.respectRamadan,
    },
  ];

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings className="w-4 h-4 text-muted-foreground" />
          Engine Configuration
        </CardTitle>
        <CardDescription className="text-xs">
          Current lock engine settings (read-only)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {configItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0"
            >
              <span className="text-sm text-muted-foreground">
                {item.label}
              </span>
              {'active' in item ? (
                <Badge
                  className={cn(
                    'text-xs',
                    item.active
                      ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400'
                      : 'bg-gray-500/15 text-gray-500 border-gray-500/30'
                  )}
                >
                  {item.value}
                </Badge>
              ) : (
                <span className="text-sm font-medium">{item.value}</span>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Overdue Summary Card ─────────────────────────────────────

function OverdueSummaryCard({
  data,
  isLoading,
}: {
  data?: OverdueDashboardModel;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const escalationCounts = data?.byEscalation ?? {};

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          Overdue Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 rounded-lg bg-red-500/5 border border-red-500/10">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {data?.totalOverdue ?? 0}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Overdue Contracts
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-orange-500/5 border border-orange-500/10">
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              ${(data?.totalAmount ?? 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Total Amount</p>
          </div>
        </div>
        {Object.keys(escalationCounts).length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              By Escalation Level
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(escalationCounts).map(([level, count]) => (
                <div key={level} className="flex items-center gap-1.5">
                  {escalationBadge(level)}
                  <span className="text-sm font-semibold">
                    {count as number}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────

function LockEnginePage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Queries
  const queueQuery = useQuery({
    queryKey: ['lock-engine', 'queue'],
    queryFn: getLockQueue,
    refetchInterval: 30_000,
  });

  const scheduleQuery = useQuery({
    queryKey: ['lock-engine', 'schedule'],
    queryFn: getLockSchedule,
    refetchInterval: 60_000,
  });

  const configQuery = useQuery({
    queryKey: ['lock-engine', 'config'],
    queryFn: getLockConfig,
    staleTime: 300_000,
  });

  const overdueQuery = useQuery({
    queryKey: ['lock-engine', 'overdue-dashboard'],
    queryFn: getOverdueDashboard,
    refetchInterval: 60_000,
  });

  // Mutations
  const processQueueMutation = useMutation({
    mutationFn: processQueue,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['lock-engine'] });
      toast({
        title: 'Queue Processed',
        description: result.message || 'Lock queue has been processed.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to process queue.',
        variant: 'destructive',
      });
    },
  });

  const processAllMutation = useMutation({
    mutationFn: processAllContracts,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['lock-engine'] });
      const v = result.value;
      toast({
        title: 'All Contracts Processed',
        description: v
          ? `Evaluated: ${v.contractsEvaluated}, Locks: ${v.locksExecuted}, Unlocks: ${v.unlocksExecuted}`
          : result.message || 'Done.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to process contracts.',
        variant: 'destructive',
      });
    },
  });

  const overdueCheckMutation = useMutation({
    mutationFn: runOverdueCheck,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['lock-engine'] });
      toast({
        title: 'Overdue Check Complete',
        description: result.message || 'Done.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to run overdue check.',
        variant: 'destructive',
      });
    },
  });

  const queueData = queueQuery.data?.value;
  const actions = queueData?.actions ?? [];
  const stats = queueData?.stats;
  const schedule = scheduleQuery.data?.value;
  const config = configQuery.data?.value;
  const overdueData = overdueQuery.data?.value;

  const isProcessing =
    processQueueMutation.isPending || processAllMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Lock Engine</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Automated device lock management with Sharia-compliant scheduling
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* The queue below shows work that is waiting. This shows work that
              stopped — an action which used every attempt is retried by nobody
              until an operator says so. */}
          <Link to="/lock-engine/automation">
            <Button variant="outline" size="sm">
              <Activity className="w-4 h-4 mr-2" />
              Automation Health
            </Button>
          </Link>
          <Link to="/lock-engine/policies">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Policies
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => overdueCheckMutation.mutate()}
            disabled={overdueCheckMutation.isPending}
          >
            {overdueCheckMutation.isPending ? (
              <ButtonSpinner className="mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Check Overdue
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => processAllMutation.mutate()}
            disabled={isProcessing}
          >
            {processAllMutation.isPending ? (
              <ButtonSpinner className="mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Process All
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Queue Total',
            value: stats?.total ?? 0,
            icon: ListChecks,
            color: 'from-blue-500 to-blue-600',
          },
          {
            label: 'Pending Actions',
            value: stats?.pending ?? 0,
            icon: Clock,
            color: 'from-amber-500 to-amber-600',
          },
          {
            label: 'Overdue Contracts',
            value: overdueData?.totalOverdue ?? 0,
            icon: AlertTriangle,
            color: 'from-red-500 to-red-600',
          },
          {
            label: 'Lock Status',
            value: schedule?.canLockNow ? 'Active' : 'Restricted',
            icon: schedule?.canLockNow ? ShieldCheck : ShieldAlert,
            color: schedule?.canLockNow
              ? 'from-emerald-500 to-emerald-600'
              : 'from-red-500 to-red-600',
          },
        ].map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${s.color} text-white shadow-lg`}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {queueQuery.isLoading ? '---' : s.value}
                </p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content: Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column: Schedule, Config, Overdue */}
        <div className="space-y-6">
          <PortfolioEscalationCard
            data={overdueData}
            isLoading={overdueQuery.isLoading}
          />
          <ScheduleCard
            schedule={schedule}
            isLoading={scheduleQuery.isLoading}
          />
          <ConfigCard config={config} isLoading={configQuery.isLoading} />
          <OverdueSummaryCard
            data={overdueData}
            isLoading={overdueQuery.isLoading}
          />
        </div>

        {/* Right Column: Queue Table */}
        <div className="lg:col-span-2">
          <Card className="shadow-md border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Lock Queue
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Pending lock/unlock actions
                    {stats &&
                      ` (${stats.pending} pending of ${stats.total} total)`}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => processQueueMutation.mutate()}
                  disabled={isProcessing || actions.length === 0}
                  className="bg-gradient-to-r from-primary to-primary/80 shadow-md"
                >
                  {processQueueMutation.isPending ? (
                    <ButtonSpinner className="mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Process Queue
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {queueQuery.isLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : actions.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <ShieldCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Queue is empty</p>
                  <p className="text-sm">
                    No pending lock actions at the moment.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead>Device</TableHead>
                        <TableHead>Contract</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Escalation</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>
                          <span className="inline-flex items-center gap-1.5">
                            Scheduled
                            <TimeZoneHint />
                          </span>
                        </TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {actions.map((action: QueuedActionModel) => (
                        <TableRow
                          key={action.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <TableCell className="font-mono text-sm">
                            #{action.deviceId}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-primary">
                            #{action.contractId}
                          </TableCell>
                          <TableCell>
                            {actionBadge(action.actionType)}
                          </TableCell>
                          <TableCell>
                            {escalationBadge(action.escalationLevel)}
                          </TableCell>
                          <TableCell
                            className="text-sm text-muted-foreground max-w-[200px] truncate"
                            title={action.reason}
                          >
                            {action.reason}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {fmtDateTime(action.scheduledFor)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {fmtDateTime(action.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
